import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到" description="抱歉，您访问的分类不存在。" />
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="px-4 md:px-6 max-w-6xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 2048核基地`,
    description: category.description,
    url: `https://pmt-pbt.cn/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '2048核基地',
      url: 'https://pmt-pbt.cn/',
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name}攻略`}
        description={category.description}
        keywords={`2048${category.name},${category.name}攻略,2048技巧`}
        canonicalUrl={`https://pmt-pbt.cn/category/${category.slug}`}
        ogType="website"
        schema={categorySchema}
      />
      <Navbar />
      <main className="pt-20 pb-20">
        <div className="px-4 md:px-6 max-w-6xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl">
                <i className={`${category.icon} text-primary-600 text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500">
                  共 {category.articleCount} 篇文章
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
              {category.description}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 flex-wrap">
            <span className="text-sm text-foreground-500 mr-1 whitespace-nowrap">切换分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-xs px-2.5 py-1 bg-accent-100 text-accent-700 rounded-md font-medium whitespace-nowrap">
                        {article.readTime}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                    </div>
                    <h2 className="text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}